import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BASE_URL_V2 } from 'shared/constants';

export interface AdminWebinarEvent {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  timezone: string;
  imageUrl?: string | null;
  isLive?: boolean;
  isCancelled?: boolean;
  type?: string;
  registrationEnabled: boolean;
  sequelEventId: string;
  dbId?: number;
  isExclusive: boolean;
  roleIds: number[];
  isActive: boolean;
  sortOrder: number;
  isStale: boolean;
  status: 'upcoming' | 'live' | 'past' | 'cancelled';
}

export interface CreateWebinarOverlayPayload {
  sequelEventId: string;
  isExclusive: boolean;
  roleIds?: number[];
  isActive?: boolean;
}

export interface UpdateWebinarOverlayPayload {
  isExclusive?: boolean;
  roleIds?: number[];
  isActive?: boolean;
}

const WEBINAR_ADMIN_KEY = ['adminWebinarEvents'];

/**
 * Raw axios functions for the webinar admin overlay endpoints.
 * Prefer the exported TanStack Query hooks in components.
 */
export const useWebinarAdminApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  /**
   * Fetches the full admin webinar list (overlay rows + unlinked Sequel events).
   * @returns {Promise<AdminWebinarEvent[]>} Admin webinar events.
   */
  const getAdminEvents = async (): Promise<AdminWebinarEvent[]> => {
    try {
      const res = await axios.get<AdminWebinarEvent[]>('/admin/webinar/events');
      if (res?.status === 200) {
        return res.data ?? [];
      }
      return Promise.reject(new Error('Failed to fetch webinar admin events'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Creates a webinar overlay row for a Sequel event.
   * @param {CreateWebinarOverlayPayload} payload - Overlay create payload.
   * @returns {Promise<AdminWebinarEvent>} Created overlay row.
   */
  const createAdminEvent = async (payload: CreateWebinarOverlayPayload): Promise<AdminWebinarEvent> => {
    try {
      const res = await axios.post<AdminWebinarEvent>('/admin/webinar/events', payload);
      if (res?.status === 201 || res?.status === 200) {
        return res.data;
      }
      return Promise.reject(new Error('Failed to create webinar overlay'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Updates an existing webinar overlay row.
   * @param {number} id - Overlay row id.
   * @param {UpdateWebinarOverlayPayload} payload - Updated overlay values.
   * @returns {Promise<AdminWebinarEvent>} Updated overlay row.
   */
  const updateAdminEvent = async (id: number, payload: UpdateWebinarOverlayPayload): Promise<AdminWebinarEvent> => {
    try {
      const res = await axios.put<AdminWebinarEvent>(`/admin/webinar/events/${id}`, payload);
      if (res?.status === 200) {
        return res.data;
      }
      return Promise.reject(new Error('Failed to update webinar overlay'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Deletes a webinar overlay row.
   * @param {number} id - Overlay row id.
   * @returns {Promise<void>}
   */
  const deleteAdminEvent = async (id: number): Promise<void> => {
    try {
      const res = await axios.delete(`/admin/webinar/events/${id}`);
      if (res?.status === 204 || res?.status === 200) {
        return;
      }
      return Promise.reject(new Error('Failed to delete webinar overlay'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Reorders overlay rows by sending overlay ids in the desired order.
   * @param {number[]} orderedIds - Overlay row ids in display order.
   * @returns {Promise<AdminWebinarEvent[]>} Refreshed admin webinar list.
   */
  const reorderAdminEvents = async (orderedIds: number[]): Promise<AdminWebinarEvent[]> => {
    try {
      const res = await axios.patch<AdminWebinarEvent[]>('/admin/webinar/events/reorder', {
        orderedIds
      });
      if (res?.status === 200) {
        return res.data ?? [];
      }
      return Promise.reject(new Error('Failed to reorder webinar overlays'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    getAdminEvents,
    createAdminEvent,
    updateAdminEvent,
    deleteAdminEvent,
    reorderAdminEvents
  };
};

/**
 * TanStack Query hook for fetching the admin webinar list.
 * @returns {UseQueryResult<AdminWebinarEvent[], AxiosError>} Query result.
 */
export const useAdminWebinarEvents = (config = {}): UseQueryResult<AdminWebinarEvent[], AxiosError> => {
  const { getAdminEvents } = useWebinarAdminApi();

  return useQuery({
    queryKey: WEBINAR_ADMIN_KEY,
    queryFn: getAdminEvents,
    staleTime: 1000 * 60,
    ...config
  });
};

/**
 * TanStack mutation hook for creating a webinar overlay row.
 */
export const useCreateWebinarOverlay = (config = {}) => {
  const { createAdminEvent } = useWebinarAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWebinarOverlayPayload) => createAdminEvent(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEBINAR_ADMIN_KEY });
    },
    ...config
  });
};

/**
 * TanStack mutation hook for updating a webinar overlay row.
 */
export const useUpdateWebinarOverlay = (config = {}) => {
  const { updateAdminEvent } = useWebinarAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateWebinarOverlayPayload }) => updateAdminEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEBINAR_ADMIN_KEY });
    },
    ...config
  });
};

/**
 * TanStack mutation hook for deleting a webinar overlay row.
 */
export const useDeleteWebinarOverlay = (config = {}) => {
  const { deleteAdminEvent } = useWebinarAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteAdminEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEBINAR_ADMIN_KEY });
    },
    ...config
  });
};

/**
 * TanStack mutation hook for reordering webinar overlay rows.
 */
export const useReorderWebinarOverlays = (config = {}) => {
  const { reorderAdminEvents } = useWebinarAdminApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: number[]) => reorderAdminEvents(orderedIds),
    onSuccess: (data) => {
      queryClient.setQueryData(WEBINAR_ADMIN_KEY, data);
    },
    ...config
  });
};
